import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronLeft, Lock, BookOpen } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader } from "@/components/cast/Common";

export function ProgressRing({ value, total, color, size = 84 }: { value: number; total: number; color: string; size?: number }) {
  const r = size / 2 - 7, c = 2 * Math.PI * r, pct = total ? value / total : 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="progress-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#EFE6D8" strokeWidth="7" fill="none" />
        <motion.circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="7" fill="none" strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - pct) }} transition={{ duration: 1.1, ease: "easeOut" }} />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center text-sm font-semibold text-slate-800"><span data-testid="progress-ring-value">{value}/{total}</span><span className="text-[10px] font-normal text-slate-500">days</span></span>
    </div>
  );
}

function PlanCard({ p }: { p: any }) {
  return (
    <Link to={`/plans/${p.id}`} data-testid={`plan-card-${p.id}`} className="card-warm flex items-center gap-5 p-6 transition-transform hover:-translate-y-0.5">
      <ProgressRing value={p.done.length} total={p.total} color={p.color} />
      <div className="min-w-0 flex-1">
        <p className="font-display text-2xl font-semibold text-slate-900">{p.title}</p>
        <p className="mt-1 text-sm text-slate-600">{p.blurb}</p>
        <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: p.color }}>
          {p.locked ? <span className="inline-flex items-center gap-1"><Lock className="h-3.5 w-3.5" />Cast Plus</span> : p.started ? (p.done_today ? "Done for today" : `Up next: day ${p.current_day}`) : "Start plan"}
        </p>
      </div>
    </Link>
  );
}

export default function Plans() {
  const { pid } = useParams();
  return pid ? <PlanDetail pid={pid} /> : <PlanList />;
}

function PlanList() {
  const [plans, setPlans] = useState<any[] | null>(null);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([])); }, []);
  if (!plans) return <Loader />;
  return (
    <div data-testid="plans-page">
      <PageHeader metaphor="Deep Water" label="Reading plans" title="A little Scripture, every day." subtitle="Pick a plan, read at your own pace, and we'll send a gentle evening reminder on days you haven't read yet." />
      {plans[0]?.locked && <div className="mb-6 rounded-2xl bg-[#1E293B] p-5 text-amber-50" data-testid="plans-plus-banner"><p className="flex items-center gap-2 font-semibold"><Lock className="h-4 w-4" />Reading plans are part of Cast Plus</p><p className="mt-1 text-sm text-amber-100/80">Cast Plus comes with the Cast app. <Link to="/plus" className="font-semibold underline" data-testid="plans-plus-link">See Cast Plus</Link></p></div>}
      <div className="grid gap-5 md:grid-cols-2">{plans.map((p) => <PlanCard key={p.id} p={p} />)}</div>
    </div>
  );
}

function PlanDetail({ pid }: { pid: string }) {
  const [plans, setPlans] = useState<any[] | null>(null);
  const [day, setDay] = useState<any>(null);
  const [n, setN] = useState<number | null>(null);
  const plan = plans?.find((p) => p.id === pid);
  useEffect(() => { api.get("/plans").then((r) => { setPlans(r.data); const p = r.data.find((x: any) => x.id === pid); if (p && !p.locked) setN(p.current_day); }); }, [pid]);
  useEffect(() => { if (n) { setDay(null); api.get(`/plans/${pid}/day/${n}`).then((r) => setDay(r.data)).catch((e) => toast.error(errMsg(e))); } }, [pid, n]);
  if (!plans) return <Loader />;
  if (!plan) return <p className="text-slate-500">Plan not found.</p>;
  const sum = day?.summary || plan;
  const markDone = async () => {
    try {
      const { data } = await api.post(`/plans/${pid}/day/${n}/done`);
      setPlans((ps) => ps!.map((p) => (p.id === pid ? data : p)));
      setDay((d: any) => ({ ...d, summary: data }));
      confetti({ particleCount: 70, spread: 70, origin: { y: 0.7 }, colors: [plan.color, "#F5C98B", "#FFFFFF"], disableForReducedMotion: true });
      toast.success(data.done.length === data.total ? "You finished the whole plan. Well done!" : `Day ${n} done. See you tomorrow!`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`plan-detail-${pid}`}>
      <Link to="/plans" className="mb-6 inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="plan-back"><ChevronLeft className="h-4 w-4" />All plans</Link>
      <div className="flex flex-wrap items-center gap-6">
        <ProgressRing value={sum.done.length} total={plan.total} color={plan.color} size={110} />
        <div><h1 className="font-display text-4xl font-semibold text-slate-900 sm:text-5xl">{plan.title}</h1><p className="mt-2 text-slate-600">{plan.blurb}</p></div>
      </div>
      {plan.locked ? (
        <div className="card-warm mt-8 p-8 text-center" data-testid="plan-locked"><Lock className="mx-auto h-8 w-8 text-slate-400" /><p className="mt-3 font-display text-2xl font-semibold">Included with Cast Plus</p><p className="mt-1 text-slate-600">Cast Plus membership is coming soon.</p></div>
      ) : (
        <>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="plan-days">
            {Array.from({ length: plan.total }, (_, i) => i + 1).map((d) => (
              <button key={d} onClick={() => setN(d)} data-testid={`plan-day-${d}`} aria-pressed={n === d}
                className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold transition-all ${n === d ? "ring-2 ring-offset-2" : ""} ${sum.done.includes(d) ? "text-white" : "bg-white text-slate-600 ring-1 ring-[#E2DBCE] hover:bg-[#FAF7F2]"}`}
                style={sum.done.includes(d) ? { background: plan.color } : undefined}>{sum.done.includes(d) ? <Check className="h-4 w-4" /> : d}</button>
            ))}
          </div>
          {!day ? <Loader /> : (
            <article className="card-warm mt-8 p-6 sm:p-10" data-testid="plan-reading">
              <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: plan.color }}>Day {day.day} · {day.abbr}</p>
              {day.readings.map((r: any) => (
                <section key={r.ref} className="mt-6"><h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-slate-900"><BookOpen className="h-5 w-5" style={{ color: plan.color }} />{r.ref}</h2>
                  <p className="mt-3 whitespace-pre-line font-scripture text-lg leading-relaxed text-slate-800">{r.text}</p></section>
              ))}
              <div className="mt-8">
                {sum.done.includes(day.day) ? <p className="inline-flex items-center gap-2 font-semibold text-[#4E9F5A]" data-testid="plan-day-complete"><Check className="h-5 w-5" />You've read day {day.day}</p>
                  : <button onClick={markDone} data-testid="plan-mark-done" className="rounded-full px-6 py-3 font-semibold text-white shadow transition-transform hover:scale-105" style={{ background: plan.color }}>Mark day {day.day} as read</button>}
              </div>
            </article>
          )}
        </>
      )}
    </div>
  );
}
